import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  ClipboardText,
  Handshake,
  SignOut,
  House,
  Users,
  UserCircle,
  ShieldCheck,
  Key,
  Database,
  UserGear
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAccount = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: '', email: '' });
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/me`);
      setProfile({ name: res.data.name || '', email: res.data.email || '' });
    } catch (e) {
      toast.error('Failed to load admin profile');
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API}/admin/me/profile`, profile);
      toast.success('Profile updated');
      if (updateUser) updateUser({ name: profile.name, email: profile.email });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSavingPwd(true);
    try {
      await axios.put(`${API}/admin/me/password`, {
        current_password: pwd.current_password,
        new_password: pwd.new_password
      });
      toast.success('Password updated');
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Heart weight="fill" className="w-6 h-6 text-primary" />
            <span className="font-serif text-xl text-white">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-stone-400 text-sm">Welcome, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-400 hover:text-white">
              <SignOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Nav */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6 overflow-x-auto">
            <Link to="/admin" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <House className="w-4 h-4 inline mr-2" />Overview
            </Link>
            <Link to="/admin/applications" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <ClipboardText className="w-4 h-4 inline mr-2" />Applications
            </Link>
            <Link to="/admin/database" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <Database className="w-4 h-4 inline mr-2" />Users
            </Link>
            <Link to="/admin/matching" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <Handshake className="w-4 h-4 inline mr-2" />Matching
            </Link>
            <Link to="/admin/team" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              <Users className="w-4 h-4 inline mr-2" />Admin Team
            </Link>
            <Link to="/admin/account" className="py-4 text-white border-b-2 border-primary text-sm font-medium whitespace-nowrap">
              <UserGear className="w-4 h-4 inline mr-2" />My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div>
          <h1 className="font-serif text-3xl text-white mb-2" data-testid="admin-account-title">My Admin Account</h1>
          <p className="text-stone-400 text-sm">Update your sign-in name, email and password. Changing your email here changes how you log in.</p>
        </div>

        {/* Profile Card */}
        <section className="bg-stone-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <UserCircle className="w-5 h-5 text-primary" />
            <h2 className="font-serif text-lg text-white">Profile</h2>
          </div>
          <form onSubmit={saveProfile} className="space-y-4">
            <div>
              <Label className="text-stone-400 text-sm">Display Name</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                data-testid="admin-account-name-input"
              />
            </div>
            <div>
              <Label className="text-stone-400 text-sm">Login Email</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                data-testid="admin-account-email-input"
              />
            </div>
            <Button type="submit" disabled={savingProfile} className="btn-primary" data-testid="admin-account-save-profile-btn">
              {savingProfile ? 'Saving…' : 'Save Profile'}
            </Button>
          </form>
        </section>

        {/* Password Card */}
        <section className="bg-stone-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <Key className="w-5 h-5 text-amber-400" />
            <h2 className="font-serif text-lg text-white">Change Password</h2>
          </div>
          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <Label className="text-stone-400 text-sm">Current Password</Label>
              <Input
                type="password"
                value={pwd.current_password}
                onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                required
                data-testid="admin-account-current-password-input"
              />
            </div>
            <div>
              <Label className="text-stone-400 text-sm">New Password (min 8 characters)</Label>
              <Input
                type="password"
                value={pwd.new_password}
                onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                required
                data-testid="admin-account-new-password-input"
              />
            </div>
            <div>
              <Label className="text-stone-400 text-sm">Confirm New Password</Label>
              <Input
                type="password"
                value={pwd.confirm}
                onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white mt-1"
                required
                data-testid="admin-account-confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={savingPwd} className="btn-primary" data-testid="admin-account-save-password-btn">
              {savingPwd ? 'Updating…' : 'Update Password'}
            </Button>
          </form>
        </section>

        {/* Lockout Recovery Tip */}
        <section className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-5 text-sm text-amber-100">
          <div className="flex items-start gap-3">
            <ShieldCheck weight="fill" className="w-5 h-5 mt-0.5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-medium mb-1">Locked Out? Two recovery paths exist:</p>
              <ol className="list-decimal list-inside space-y-1 text-amber-200/90">
                <li>Use <strong>Admin Team</strong> to add a second admin you trust — they can reset your password if you ever lock yourself out.</li>
                <li>If <em>all</em> admins are locked out, the site owner can update <code>ADMIN_EMAIL</code> and <code>ADMIN_PASSWORD</code> in the backend <code>.env</code> file and restart the backend — that matching account's password is automatically restored on boot.</li>
              </ol>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminAccount;
